import { ClassificationResult, ErrorCode, OtherDocumentType } from '@documents/types'
import { IncomeType } from '@frame/lib/types'

export const DEFAULT_MAX_FILE_SIZE_BYTES = 1024 * 1024 * 50 // 50 MiB
export const DEFAULT_MAX_FILES = 10

const FILES_TOO_LARGE_MESSAGE = `You may only upload ${
  DEFAULT_MAX_FILES > 1 ? 'up to ' + DEFAULT_MAX_FILES + ' files at once' : DEFAULT_MAX_FILES + ' file'
}, and each must be under ${(DEFAULT_MAX_FILE_SIZE_BYTES / 1024 / 1024).toFixed(0)} MiB`

export const ERROR_CODE_MESSAGES = {
  [ErrorCode.FILE_INVALID_TYPE]: 'File type not accepted',
  [ErrorCode.FILE_TOO_LARGE]: FILES_TOO_LARGE_MESSAGE,
  [ErrorCode.TOO_MANY_FILES]: FILES_TOO_LARGE_MESSAGE,
  [ErrorCode.DUPLICATE_FILE]: `Cannot upload duplicate file`,
  [ErrorCode.OTHER]: 'An unknown error occurred',
}

export const UPLOAD_STEP_CONFIG = {
  [IncomeType.EMPLOYED]: {
    desc: {
      plain: 'Upload the last 30 days of paystubs from your employer',
      bold: [],
    },
  },
  [IncomeType.OTHER]: {
    desc: {
      plain:
        'Upload any supporting documents you have such as bank statements, retirement accounts, or statement of benefits from agency or company.',
      bold: ['bank statements', 'retirement accounts', 'statement of benefits from agency or company'],
    },
  },
}

export const IMAGE_FILE_TYPES = ['.pdf', '.png', '.jpeg', '.jpg', '.bmp', '.tif', '.tiff', '.heic', '.heif']
export const PDF_FILE_TYPES = ['.pdf']

export const SUPPORTED_CLASSIFICATION_TYPES = [
  ClassificationResult.PAYSTUB,
  ClassificationResult.W2,
  ClassificationResult.UNKNOWN,
]

export const CLASSIFICATION_TYPE_LABELS: Partial<Record<ClassificationResult, string>> = {
  [ClassificationResult.A_1005]: 'Form 1005',
  [ClassificationResult.A_1040_2023]: 'Form 1040 (2023)',
  [ClassificationResult.A_1040_2024]: 'Form 1040 (2024)',
  [ClassificationResult.A_1040_SCHEDULE_1_2023]: 'Form 1040 Schedule 1 (2023)',
  [ClassificationResult.A_1040_SCHEDULE_1_2024]: 'Form 1040 Schedule 1 (2024)',
  [ClassificationResult.A_1040_SCHEDULE_2_2024]: 'Form 1040 Schedule 2 (2024)',
  [ClassificationResult.A_1040_SCHEDULE_3_2023]: 'Form 1040 Schedule 3 (2023)',
  [ClassificationResult.A_1040_SCHEDULE_3_2024]: 'Form 1040 Schedule 3 (2024)',
  [ClassificationResult.A_1040_SCHEDULE_8812_2023]: 'Form 1040 Schedule 8812 (2023)',
  [ClassificationResult.A_1040_SCHEDULE_8812_2024]: 'Form 1040 Schedule 8812 (2024)',
  [ClassificationResult.A_1040_SCHEDULE_A_2024]: 'Form 1040 Schedule A (2024)',
  [ClassificationResult.A_1040_SCHEDULE_B_2024]: 'Form 1040 Schedule B (2024)',
  [ClassificationResult.A_1040_SCHEDULE_C_2024]: 'Form 1040 Schedule C (2024)',
  [ClassificationResult.A_1040_SCHEDULE_D_2024]: 'Form 1040 Schedule D (2024)',
  [ClassificationResult.A_1040_SCHEDULE_EIC_2024]: 'Form 1040 Schedule EIC (2024)',
  [ClassificationResult.A_1040_SCHEDULE_SE_2024]: 'Form 1040 Schedule SE (2024)',
  [ClassificationResult.A_1065_SCHEDULE_K_1_2024]: 'Form 1065 Schedule K-1 (2024)',
  [ClassificationResult.A_1099_G]: 'Form 1099-G',
  [ClassificationResult.A_1099_INT]: 'Form 1099-INT',
  [ClassificationResult.A_1099_K]: 'Form 1099-K',
  [ClassificationResult.A_1099_MISC]: 'Form 1099-MISC',
  [ClassificationResult.A_1099_NEC]: 'Form 1099-NEC',
  [ClassificationResult.A_1099_R]: 'Form 1099-R',
  [ClassificationResult.A_4562]: 'Form 4562',
  [ClassificationResult.A_8863]: 'Form 8863',
  [ClassificationResult.A_8867]: 'Form 8867',
  [ClassificationResult.A_8879]: 'Form 8879',
  [ClassificationResult.A_8962]: 'Form 8962',
  [ClassificationResult.A_8995]: 'Form 8995',
  [ClassificationResult.A_9325]: 'Form 9325',
  [ClassificationResult.A_941_X]: 'Form 941-X',
  [ClassificationResult.BANK_ACCOUNT]: 'bank account',
  [ClassificationResult.BANK_STATEMENT]: 'bank statement',
  [ClassificationResult.BROKERAGE_STATEMENT]: 'brokerage statement',
  [ClassificationResult.CHARITABLE_DEDUCTION_LIMITS_WORKSHEET]: 'Charitable Deduction Limits Worksheet',
  [ClassificationResult.COVER_PAGE]: 'cover page',
  [ClassificationResult.CREDIT_CARD_STATEMENT]: 'credit card statement',
  [ClassificationResult.DEPOSITED_CHECK]: 'deposited check',
  [ClassificationResult.DISABILITY_AWARD_LETTER]: 'Disability Award Letter',
  [ClassificationResult.ELECTRONIC_RECORD_AND_SIGNATURE_DISCLOSURE]: 'Electronic Record and Signature Disclosure',
  [ClassificationResult.FEDERAL_INFORMATION_WORKSHEET]: 'Federal Information Worksheet',
  [ClassificationResult.FEDERAL_TAX_RETURN_WORKSHEET]: 'Federal Tax Return Worksheet',
  [ClassificationResult.FILING_INSTRUCTIONS]: 'filing instructions',
  [ClassificationResult.HOA_CCRS]: 'HOA CC&Rs',
  [ClassificationResult.INCOMING_WIRE_DIRECT_DEPOSIT_CONFIRMATION]: 'Incoming Wire/Direct Deposit Confirmation',
  [ClassificationResult.INVOICE]: 'Invoice',
  [ClassificationResult.LETTER_OF_EXPLANATION]: 'Letter of Explanation',
  [ClassificationResult.LLC_CORPORATION_DOCUMENTS]: 'LLC/Corporation Documents',
  [ClassificationResult.PAYOFF_STATEMENT]: 'payoff statement',
  [ClassificationResult.PAYSTUB]: 'paystub',
  [ClassificationResult.PROFIT_AND_LOSS]: 'profit and loss statement',
  [ClassificationResult.SOLAR_PANEL_LEASE_AGREEMENT]: 'Solar panel lease agreement',
  [ClassificationResult.SS_AWARD_LETTER]: 'Social Security Award Letter',
  [ClassificationResult.STATE_TAX_RETURN]: 'State Tax Return',
  [ClassificationResult.TAX_RETURN_TRANSCRIPT]: 'Tax Return Transcript',
  [ClassificationResult.TRUST_AGREEMENT_OR_TRUST_CERTIFICATION]: 'Trust Agreement or Trust Certification',
  [ClassificationResult.UNKNOWN]: 'Unknown',
  [ClassificationResult.VA_AWARD_LETTER]: 'VA Award Letter',
  [ClassificationResult.VERIFICATION_OF_EMPLOYMENT]: 'Verification of Employment',
  [ClassificationResult.W2]: 'W2',
  [ClassificationResult.OFFER_LETTER]: 'Offer letter',
}

export const OTHER_DOCUMENT_TYPE_LABEL: Record<OtherDocumentType, string> = {
  [OtherDocumentType.ALIMONY]: 'Alimony Agreement / Court Order',
  [OtherDocumentType.CHILD_SUPPORT]: 'Child Support Payment Record',
  [OtherDocumentType.DISABILITY_BENEFITS]: 'Disability Benefits Statement',
  [OtherDocumentType.HOUSING_VOUCHER]: 'Housing Voucher or Rental Subsidy Award Letter',
  [OtherDocumentType.OFFER_LETTER]: 'Signed Offer Letter from Employer',
  [OtherDocumentType.VA_BENEFITS]: 'Veterans Affairs (VA) Benefits Letter',
  [OtherDocumentType.OTHER]: 'Other',
}
